import {Injectable} from '@angular/core';
import {ApiService} from "../../services/api/api.service";
import {EMPTY, from, Observable, of, Subject, throwError} from "rxjs";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {catchError, concatMap, map, mergeMap, switchMap, take, tap, toArray} from "rxjs/operators";
import {SearchQueryOptions} from "../../services/api/search-query-options";
import {COMPONENT_EVENTS_CONFIG} from "../../_typing/config/component-events-table";
import {
    ExporterType
} from "../../forms/component-events-table-form/component-events-table-form.component";
import {QueryBuilderService} from "../../services/query_builder.service";
import {ConstantProperty} from "../../_models/constant-property";
import {FormDialogService} from "../../services/form-dialog.service";

import {getBaseFilter, getRelationWithIdFilter} from "../../services/api/filter_utils";
import {
    ConstantRelationshipAggregationRequest,
    ConstantValue,
    GenericConstantApiResponse
} from "../../_models/api/generic-constant";
import {GenericConstantDataService} from "../../data/generic-constant-data.service";
import {IDMap, KeyMap, ModelID, RESTFilter, ModelWithLimits} from '../../_typing/generic-types';
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {EventConfigColumn} from "../../_typing/config/config-column";
import {NotificationService} from "../../services/notification.service";
import {Component, WireComponent} from "../../_models/component";
import {WireEvent} from "../../_models/event";
import {BackgroundJobService, JobPollResponse} from '../../services/background-job.service';
import {ConstantPropertyComponentType} from "../../_models/constant-property-component-type";
import {detectLimits} from "../../lib/utils";
import {EXTENDED_NOTIFICATION_DURATION_MS, NOTIFICATION_DURATION_MS} from "../../shared/globals";
import {FILE_DOWNLOAD_OPTIONS, FileDownloadOption} from "../../_typing/download-filetype";
import {CustomDialogService} from "../../services/custom-dialog.service";
import {ProcessQuickMenuComponent} from "../../components/process-quick-menu/process-quick-menu.component";
import {
    ComponentEventsDownloadFormatMenuComponent
} from "../../forms/custom-dialog/events-download-format-menu/component-events-download-format-menu.component";
import {PopupService} from "../../services/popup-service";

export interface UpdateComponentConstantCalculationsResponse {
    message: string;
    warning?: boolean;
}

/** Minimal config fields required to build a {@link ConstantDateFilter}.
 * Satisfied by both COMPONENT_EVENTS_CONFIG and CUSTOM_EVENTS_CONFIG.
 */
export interface ConstantDateFilterConfig {
    start_prop?: string;
    end_prop?: string;
    include_null_constant_dates?: boolean;
}

/** Shape returned by {@link ComponentEventsTableService.buildConstantDateFilter}
 * and accepted by download endpoints that support server-side date-range filtering.
 */
export interface ConstantDateFilter {
    start_prop_id: ModelID | null;
    end_prop_id: ModelID | null;
    dtp_start: Date;
    dtp_end: Date;
    exclude_null_start: boolean;
    exclude_null_end: boolean;
}

/** Paginated response shape returned by the component list endpoints. */
interface ComponentPageResponse {
    data: Array<{ id: ModelID }>;
    meta?: { count?: number };
}

/** Default number of items per page for standard paginated component queries. */
const DEFAULT_PAGE_SIZE = 50;

/** Page size used when pre-resolving component IDs for bulk file downloads.
 * Larger than DEFAULT_PAGE_SIZE to reduce total request count while staying
 * within server payload limits. */
const DOWNLOAD_ID_FETCH_PAGE_SIZE = 500;

/** Maximum number of concurrent HTTP requests when paginating through component
 * IDs for bulk downloads. Caps the fan-out from {@link getFilteredComponentIdsForDownload}
 * to prevent backend overload on large result sets. */
const DOWNLOAD_ID_FETCH_CONCURRENCY = 4;

@Injectable({
    providedIn: 'root'
})
export class ComponentEventsTableService {
    $componentSaved: Subject<{ component: WireComponent, custom_constant_dict: KeyMap<ConstantValue> }> = new Subject();
    $refreshComponentEventsTable: Subject<void> = new Subject();
    /** Emits once when a bulk file download job finishes (success or error). */
    jobComplete$ = new Subject<void>();

    constructor(private api: ApiService,
                private http: HttpClient,
                private notification: NotificationService,
                private queryBuilderService: QueryBuilderService,
                private formDialogService: FormDialogService,
                private customDialogService: CustomDialogService,
                private genericConstantData: GenericConstantDataService,
                private jobService: BackgroundJobService,
                private popupService: PopupService) {
    }

    /** Ensures `include_null_constant_dates` is set on configs that pre-date the
     * field, preserving the historical default for each tile type.
     *
     * - `COMPONENT_EVENTS` tiles: pass `defaultValue = true` (nulls were always
     *   included before the field was introduced).
     * - `CUSTOM_EVENTS` tiles: pass `defaultValue = false` (historical code gated
     *   on `if (config.include_cp_nulls)`, so absent meant excluded).
     *
     * If the legacy key `include_cp_nulls` is present and the new key is absent,
     * the value is migrated across and the old key removed regardless of `defaultValue`.
     *
     * @param config - The tile config to normalise in place.
     * @param defaultValue - Value to apply when neither key is present. Defaults
     *   to `true` for backwards compatibility with `COMPONENT_EVENTS` callers.
     */
    static ensureIncludeNullConstantDatesDefault(config: ConstantDateFilterConfig, defaultValue: boolean = true): ConstantDateFilterConfig {
        if (config.include_null_constant_dates === undefined) {
            const legacy = (config as Record<string, unknown>)['include_cp_nulls'];
            if (legacy !== undefined) {
                config.include_null_constant_dates = legacy as boolean;
                delete (config as Record<string, unknown>)['include_cp_nulls'];
            } else {
                config.include_null_constant_dates = defaultValue;
            }
        }
        return config;
    }

    static buildConstantDateFilter(config: ConstantDateFilterConfig, dtp: IDateTimePeriod): ConstantDateFilter {
        const includeNullDates = config.include_null_constant_dates === true;
        return {
            start_prop_id: config.start_prop || null,
            end_prop_id: config.end_prop || null,
            dtp_start: dtp.start,
            dtp_end: dtp.end,
            exclude_null_start: !includeNullDates,
            exclude_null_end: !includeNullDates,
        };
    }

    updateComponentConstantCalculations(componentIds: string[], constantPropertyIds?: string[]): Observable<UpdateComponentConstantCalculationsResponse> {
        const payload: any = {'component_id_list': componentIds};
        // An explicit empty selection must stay a backend no-op; omitting the
        // key would escalate the request to every property of the components.
        if (constantPropertyIds) {
            payload['constant_property_list'] = constantPropertyIds;
        }
        return this.http.patch<UpdateComponentConstantCalculationsResponse>('/api/UpdateComponentConstantCalculations', payload).pipe(catchError(({error}) => {
            console.log('error', error);
            if (error && error.message) {
                if (typeof error.message === 'object') {
                    Object.values(error.message).forEach((errorMessage: string) => {
                        this.notification.openError(errorMessage);
                    });
                }
            }

            return throwError(error);
        }));
    }

    getComponentsFilter(config: COMPONENT_EVENTS_CONFIG, dtp: IDateTimePeriod, search_by_name: string,
                        user_filters: RESTFilter, query_builder_filters: RESTFilter) {
        const options = new SearchQueryOptions();
        options.filters = [
            getRelationWithIdFilter('component_type', config.selected_component_type.id)
        ];

        if (config.query) {
            options.filters.push(this.queryBuilderService.toJsonQuery(config.query));
        }

        if (config.search_by_name === true) {
            if (!search_by_name) {
                this.notification.openError('Please enter a name to search by in the search box', NOTIFICATION_DURATION_MS);
                return;
            }
            options.filters.push(
                {op: 'ilike', name: 'name', val: '%' + search_by_name + '%'}
            );
        }
        if (config.name_must_contain) {
            options.filters.push(
                {op: 'ilike', name: 'name', val: '%' + config.name_must_contain + '%'}
            );
        }
        if (config.filter_by_created_on === true) {
            // let d = moment(this.dateTimePeriodService.dtp.start).add(-5, 'days');
            options.filters.push(
                {op: 'ge', name: 'created_on', val: dtp.start},
                {op: 'le', name: 'created_on', val: dtp.end}
            );
        }
        if (config.exclude_null_start && config.exclude_null_end) {
            options.filters.push({
                and: [
                    {"name": "start_time", "op": "ne", val: null},
                    {"name": "end_time", "op": "ne", val: null}
                ]
            });
        }
        if (config.bypass_date_filters !== true && ((!config?.constant_property_time && (
                config.selected_cols?.component_type.filter(c => !c.related_type_id)?.map(item => item.id).includes('start_time') ||
                config.selected_cols?.component_type.filter(c => !c.related_type_id)?.map(item => item.id).includes('end_time')
            )) ||
            (config?.constant_property_time && (config.start_prop || config.end_prop)
            ))) {
            let dateQuery = this.getComponentFilterByConstantValueLogic(config, dtp);
            options.filters.push(dateQuery);
        }
        if (user_filters) {
            options.filters.push(user_filters);
        }
        if (query_builder_filters) {
            options.filters.push(query_builder_filters);
        }

        return options.filters;

    }

    getComponentsFilterNonDate(config: COMPONENT_EVENTS_CONFIG, dtp: IDateTimePeriod, search_by_name: string,
                               user_filters: RESTFilter, query_builder_filters: RESTFilter): RESTFilter {
        const filters: any[] = [
            getRelationWithIdFilter('component_type', config.selected_component_type.id)
        ];

        if (config.query) {
            filters.push(this.queryBuilderService.toJsonQuery(config.query));
        }

        if (config.search_by_name === true) {
            if (!search_by_name) {
                // Return an empty sentinel array; the caller is responsible for
                // notifying the user. Using an empty array avoids a double toast
                // when this method is called indirectly via getComponentsByConstantDateFilter.
                return [];
            }
            filters.push({op: 'ilike', name: 'name', val: '%' + search_by_name + '%'});
        }

        if (config.name_must_contain) {
            filters.push({op: 'ilike', name: 'name', val: '%' + config.name_must_contain + '%'});
        }

        if (config.filter_by_created_on === true) {
            filters.push(
                {op: 'ge', name: 'created_on', val: dtp.start},
                {op: 'le', name: 'created_on', val: dtp.end}
            );
        }

        if (config.exclude_null_start && config.exclude_null_end) {
            filters.push({
                and: [
                    {"name": "start_time", "op": "ne", val: null},
                    {"name": "end_time", "op": "ne", val: null}
                ]
            });
        }

        // Add component attribute date filtering (start_time/end_time) if not using constant properties
        if (config.bypass_date_filters !== true &&
            !config?.constant_property_time &&
            (config.selected_cols?.component_type.filter(c => !c.related_type_id)?.map(item => item.id).includes('start_time') ||
                config.selected_cols?.component_type.filter(c => !c.related_type_id)?.map(item => item.id).includes('end_time'))) {
            let dateQuery = this.getComponentFilterByConstantValueLogic(config, dtp);
            filters.push(dateQuery);
        }

        if (user_filters) {
            filters.push(user_filters);
        }

        if (query_builder_filters) {
            filters.push(query_builder_filters);
        }

        return filters;
    }

    /** Returns true when {@link getComponentsFilterNonDate} signalled that
     * search_by_name is required but was left blank. Callers should abort
     * their operation and return early without showing an additional toast.
     */
    isNameSearchAbortSentinel(filters: RESTFilter): boolean {
        return Array.isArray(filters) && (filters as any[]).length === 0;
    }

    shouldUseUpdatedFiltering(config: COMPONENT_EVENTS_CONFIG): boolean {
        if (!config?.constant_property_time) {
            return false;
        }

        if (config.bypass_date_filters === true) {
            return false;
        }

        // Optimisation is beneficial when filtering by constant property dates
        return !!(config.start_prop || config.end_prop);
    }

    getComponentsByConstantDateFilter(
        baseType: string,
        config: COMPONENT_EVENTS_CONFIG,
        dtp: IDateTimePeriod,
        search_by_name: string,
        user_filters: RESTFilter,
        query_builder_filters: RESTFilter,
        page_number: number = 1,
        page_size: number = DEFAULT_PAGE_SIZE,
        sort: string = 'name'
    ): Observable<ComponentPageResponse> {
        const constantDateFilter = ComponentEventsTableService.buildConstantDateFilter(config, dtp);

        const otherFilters = this.getComponentsFilterNonDate(
            config, dtp, search_by_name, user_filters, query_builder_filters
        );

        if (this.isNameSearchAbortSentinel(otherFilters)) {
            // getComponentsFilterNonDate returns [] when search_by_name is required
            // but blank. Show a single validation message here and complete without
            // error so that catchError downstream does not fire a second toast.
            this.notification.openError('Please enter a name to search by in the search box', NOTIFICATION_DURATION_MS);
            return EMPTY;
        }

        let params = new HttpParams();
        params = params.set('constant_date_filter', JSON.stringify(constantDateFilter));

        if (otherFilters && otherFilters.length > 0) {
            params = params.set('filter', JSON.stringify(otherFilters));
        }

        params = params.set('page[number]', page_number.toString());
        params = params.set('page[size]', page_size.toString());
        params = params.set('sort', sort);

        let endpoint = '/api/component';
        if (baseType === 'resource' || baseType === 'equipment') {
            endpoint = `/api_min/${baseType}_light`;
        } else if (baseType) {
            endpoint = `/api/${baseType}`;
        }

        return this.http.get<ComponentPageResponse>(endpoint, {params}).pipe(
            catchError(error => {
                console.error('Optimised filtering failed:', error);
                this.notification.openError('Failed to load components. Please try again.');
                return throwError(error);
            })
        );
    }

    /** Fetches all component IDs matching the constant-date filter, for use when
     * the downstream endpoint (e.g. BulkComponentFileDownload) does not support
     * a constant_date_filter param and IDs must be pre-resolved on the frontend.
     *
     * Paginates through all pages so that the result is never silently truncated.
     * Returns an empty array when the first page reports zero total results.
     */
    getFilteredComponentIdsForDownload(
        baseType: string,
        config: COMPONENT_EVENTS_CONFIG,
        dtp: IDateTimePeriod,
        search_by_name: string,
        user_filters: RESTFilter,
        query_builder_filters: RESTFilter,
    ): Observable<ModelID[]> {
        const fetchPage = (page: number): Observable<ComponentPageResponse> =>
            this.getComponentsByConstantDateFilter(
                baseType, config, dtp, search_by_name, user_filters, query_builder_filters,
                page, DOWNLOAD_ID_FETCH_PAGE_SIZE
            );

        return fetchPage(1).pipe(
            switchMap((firstResult: ComponentPageResponse) => {
                const ids: ModelID[] = (firstResult?.data ?? []).map(c => c.id);
                const total: number = firstResult?.meta?.count ?? ids.length;
                const totalPages = Math.ceil(total / DOWNLOAD_ID_FETCH_PAGE_SIZE);

                if (totalPages <= 1) {
                    return of(ids);
                }

                const remainingPages = Array.from({length: totalPages - 1}, (_, i) => i + 2);
                return from(remainingPages).pipe(
                    mergeMap(page => fetchPage(page), DOWNLOAD_ID_FETCH_CONCURRENCY),
                    toArray(),
                    map((results: ComponentPageResponse[]) => {
                        const extraIds = results.flatMap(r => (r?.data ?? []).map(c => c.id));
                        return [...ids, ...extraIds];
                    })
                );
            })
        );
    }

    getApiComponentConstants(component_ids, constant_properties_ids, attributes?: string[], relationshipMap?: IDMap<ModelID[]>, relationshipAttributeMap?: IDMap<string[]>, aggregations?: ConstantRelationshipAggregationRequest[], showAggregations?: boolean, componentTypeRelationships?: IDMap<any>): Observable<GenericConstantApiResponse> {
        return this.genericConstantData.getComponentConstants(component_ids, constant_properties_ids, attributes, relationshipMap, relationshipAttributeMap, aggregations, showAggregations, componentTypeRelationships);
    }

    getQueryWarningTooltip(config: COMPONENT_EVENTS_CONFIG, cp_dict: IDMap<ConstantProperty>): string {
        let tooltip = "";
        if (config.query) {
            tooltip += "This table is using a custom query.";
        }

        if (config.constant_property_time && (config.start_prop || config.end_prop)) {
            tooltip += " This table is filtered by date using start property " +
                (cp_dict[config.start_prop]?.attributes.name || "(Not set)") + " and end property " +
                (cp_dict[config.end_prop]?.attributes.name || "(Not set)");
        }
        tooltip += ". Click to open the table form.";
        return tooltip;
    }

    addTimeFilterProperties(config, prop_ids: string[]): string[] {
        if (config.constant_property_time) {
            if (config.start_prop && !prop_ids.includes(config.start_prop)) {
                prop_ids.push(config.start_prop);
            }
            if (config.end_prop && !prop_ids.includes(config.end_prop)) {
                prop_ids.push(config.end_prop);
            }
        }
        return prop_ids;
    }

    getComponentFilterByConstantValueLogic(config: COMPONENT_EVENTS_CONFIG, dtp: IDateTimePeriod) {
        let date_filter_logic;
        let start, end, dateRanges;
        if (config.constant_property_time && (config.start_prop || config.end_prop)) {
            start = !!config.start_prop;
            end = !!config.end_prop;
            dateRanges = this.getDateFilterConstantProperty(config, dtp);
        } else {
            start = config.selected_cols.component_type.filter(c => !c.related_type_id)?.map((item: EventConfigColumn) => item.id).includes('start_time');
            end = config.selected_cols.component_type.filter(c => !c.related_type_id)?.map((item: EventConfigColumn) => item.id).includes('end_time');
            dateRanges = this.getDateFilterComponentAttribute(config, dtp);
        }

        const {startInRange, endInRange, overlap} = dateRanges;

        if (start && end) {
            date_filter_logic = {or: [startInRange, endInRange, overlap]};
        } else if (start) {
            date_filter_logic = startInRange;
        } else if (end) {
            date_filter_logic = endInRange;
        }

        return date_filter_logic;
    }

    private getDateFilterComponentAttribute(config: COMPONENT_EVENTS_CONFIG, dtp: IDateTimePeriod) {
        const startInRange: { or: any[] } = {
            or: [{
                "and": [
                    {"name": "start_time", "op": "ge", "val": dtp.start},
                    {"name": "start_time", "op": "le", "val": dtp.end}
                ]
            }]
        };
        if (!config.exclude_null_start) {
            startInRange['or'].push({"name": "start_time", "op": 'eq', val: null});
        }
        const endInRange: { or: any[] } = {
            or: [{
                "and": [
                    {"name": "end_time", "op": "ge", "val": dtp.start},
                    {"name": "end_time", "op": "le", "val": dtp.end}
                ]
            }]
        };
        if (!config.exclude_null_end) {
            endInRange['or'].push({"name": "end_time", "op": 'eq', val: null});
        }
        // Neither start nor end fall within dtp but dtp falls within custom_constant range
        const overlap = {
            "and": [
                {"name": "start_time", "op": "lt", "val": dtp.start},
                {"name": "end_time", "op": "gt", "val": dtp.end}
            ]
        };

        return {startInRange, endInRange, overlap};
    }

    private getDateFilterConstantProperty(config: COMPONENT_EVENTS_CONFIG, dtp: IDateTimePeriod) {
        const includeNulls = config.include_null_constant_dates === true;

        const startOrConditions: RESTFilter = [
            this.generateComponentConstantDateFilter(config.start_prop, dtp, includeNulls)
        ];
        if (includeNulls) {
            // Include components that don't have the Constant
            startOrConditions.unshift({
                "not": {
                    "name": "constant_components",
                    "op": "any",
                    "val": {
                        "name": "constant_property",
                        "op": "has",
                        "val": {"name": "id", "op": "eq", "val": config.start_prop}
                    }
                }
            });
        }
        const startInRange = {"or": startOrConditions};

        const endOrConditions: RESTFilter = [
            this.generateComponentConstantDateFilter(config.end_prop, dtp, includeNulls)
        ];
        if (includeNulls) {
            // Include components that don't have the Constant
            endOrConditions.unshift({
                "not": {
                    "name": "constant_components",
                    "op": "any",
                    "val": {
                        "name": "constant_property",
                        "op": "has",
                        "val": {"name": "id", "op": "eq", "val": config.end_prop}
                    }
                }
            });
        }
        const endInRange = {"or": endOrConditions};

        // Neither start nor end fall within dtp but dtp falls within custom_constant range
        const overlap = this.generateDtpBetweenDatesFilter(config.start_prop, config.end_prop, dtp);

        return {startInRange, endInRange, overlap};
    }

    generateComponentConstantDateFilter(datePropID: ModelID, dtp: IDateTimePeriod, includeNulls: boolean = true) {
        let filter: any = {
            "name": "constant_components",
            "op": "any",
            "val": {
                "and": [
                    {
                        "name": "constant_property",
                        "op": "has",
                        "val": {"name": "id", "op": "eq", "val": datePropID}
                    },
                    {
                        "or": [
                            {
                                "and": [
                                    {"name": "value", "op": "ne", "val": "None"},
                                    {"name": "value_date", "op": "ge", "val": dtp.start},
                                    {"name": "value_date", "op": "le", "val": dtp.end}
                                ]
                            }
                        ]
                    }
                ]
            }
        }
        if (includeNulls) {
            filter.val.and[1].or.push({
                // include component that have the Constant as either null/"None"
                "or": [
                    {"name": "value", "op": 'eq', val: null},
                    {"name": "value", "op": "eq", "val": "None"},
                ]
            });
        }
        return filter;
    }

    generateDtpBetweenDatesFilter(startPropID: ModelID, endPropID: ModelID, dtp: IDateTimePeriod): any {
        return {
            "and": [
                {
                    "name": "constant_components",
                    "op": "any",
                    "val": {
                        "and": [
                            {
                                "name": "constant_property",
                                "op": "has",
                                "val": {"name": "id", "op": "eq", "val": startPropID}
                            },
                            {"name": "value", "op": "ne", "val": "None"},
                            {"name": "value_date", "op": "lt", "val": dtp.start}
                        ]
                    }
                },
                {
                    "name": "constant_components",
                    "op": "any",
                    "val": {
                        "and": [
                            {
                                "name": "constant_property",
                                "op": "has",
                                "val": {"name": "id", "op": "eq", "val": endPropID}
                            },
                            {"name": "value", "op": "ne", "val": "None"},
                            {"name": "value_date", "op": "gt", "val": dtp.end}
                        ]
                    }
                }
            ]
        }
    }

    getComponentsForComponentFilters(first_component_id: string, relationship_component_type): any {
        const options = new SearchQueryOptions();
        const base_type = relationship_component_type.attributes.base_type;
        const ct_id = relationship_component_type.id;
        options.filters = [{
            or: [{
                and: [{
                    name: 'component_component',
                    op: 'any',
                    val: getRelationWithIdFilter('first_component', first_component_id)
                },
                    getRelationWithIdFilter('component_type', ct_id),
                    {name: 'base_type', op: 'eq', val: base_type},
                ]
            }, {
                and: [{
                    name: 'component_component',
                    op: 'any',
                    val: getRelationWithIdFilter('second_component', first_component_id)
                },
                    getRelationWithIdFilter('component_type', ct_id),
                    {name: 'base_type', op: 'eq', val: base_type}
                ]
            }
            ]
        }, {name: 'id', op: 'ne', val: first_component_id}];
        return options.filters;
    }

    /** Build a server-side filter clause for a single user-selected search column.
     * Dispatches on column type so attribute, constant_property, related-type
     * constant_property and component_type columns each get an appropriate query. */
    buildUserSearchFilter(column: EventConfigColumn, filterValue: string): any | null {
        if (column.related_type === 'component_type' && column.related_type_id) {
            if (column.type === 'attribute') {
                const attributeMatch = column.id === 'changed_by'
                    ? this.buildChangedBySearchFilter(filterValue)
                    : {op: 'ilike', name: column.id, val: filterValue};
                return this.buildLinkedComponentFilter(column.related_type_id, attributeMatch);
            }
            if (column.type === 'constant_property') {
                return this.buildLinkedComponentFilter(column.related_type_id,
                    this.buildConstantPropertySearchFilter(column.id, filterValue));
            }
        }

        if (column.type === 'attribute') {
            if (column.id === 'changed_by') {
                return this.buildChangedBySearchFilter(filterValue);
            }
            // name / start_time / end_time - ilike on the attribute directly.
            return {op: 'ilike', name: column.id, val: filterValue};
        }

        if (column.type === 'constant_property') {
            return this.buildConstantPropertySearchFilter(column.id, filterValue);
        }

        if (column.type === 'component_type') {
            return this.buildLinkedComponentFilter(column.id,
                {op: 'ilike', name: 'name', val: filterValue});
        }

        return null;
    }

    private buildChangedBySearchFilter(filterValue: string): any {
        return {
            name: 'changed_by',
            op: 'has',
            val: {
                or: [
                    {op: 'ilike', name: 'name', val: filterValue},
                    {op: 'ilike', name: 'username', val: filterValue},
                ],
            },
        };
    }

    private buildConstantPropertySearchFilter(columnId: ModelID, filterValue: string): any {
        return {
            name: 'constant_components',
            op: 'any',
            val: {
                and: [
                    {name: 'constant_property', op: 'has', val: getBaseFilter(columnId, 'id')},
                    getBaseFilter(filterValue, 'value', 'ilike'),
                ],
            },
        };
    }

    /** Wrap a match against a linked component (reached via component_component)
     * so that the inner filter applies to either side of the relationship and
     * only to components of the supplied component_type id. */
    private buildLinkedComponentFilter(linkedComponentTypeId: ModelID, innerComponentMatch: any): any {
        const componentTypeMatch = {
            name: 'component_type',
            op: 'has',
            val: getBaseFilter(linkedComponentTypeId, 'id'),
        };
        const sideFilter = (side: 'first_component' | 'second_component') => ({
            name: 'component_component',
            op: 'any',
            val: {
                name: side,
                op: 'has',
                val: {and: [componentTypeMatch, innerComponentMatch]},
            },
        });
        return {or: [sideFilter('first_component'), sideFilter('second_component')]};
    }

    getConstantPropertiesSearchFilter(constantPropertiesNames, cp_name_dict, filterString) {
        const constantPropertiesFilters = [];
        constantPropertiesNames.forEach(name => {
            if (cp_name_dict[name]?.attributes.data_type !== 'datetime') {
                constantPropertiesFilters.push({
                    and: [
                        {
                            "name": "constant_components",
                            "op": "any",
                            "val": {
                                "and": [
                                    {
                                        "name": "constant_property",
                                        "op": "has",
                                        "val": {"name": "name", "op": "eq", "val": name}
                                    },
                                    {
                                        "name": "_data",
                                        "op": "ilike",
                                        "val": `%${filterString}%`
                                    }]
                            }
                        }]
                });
            }
        });

        return constantPropertiesFilters;
    }

    getConstantConditionalFormat(item_id, cp_class_dict, cp: ConstantProperty, column: EventConfigColumn, limits_map, value, show_error = true) {
        if (isNaN(value) || !cp) {
            return cp_class_dict;
        }
        let limits = limits_map[cp.id]?.attributes;
        let hi = limits?.hi;
        let hihi = limits?.hihi;
        let low = limits?.low;
        let lowlow = limits?.lowlow;
        if (!((lowlow || lowlow === 0) && (low || low === 0) && (hihi || hihi === 0) && (hi || hi === 0) && (value || value === 0))) {
            return cp_class_dict;
        }
        let value_class = '';
        if (value > hihi) {
            value_class = 'value-error';
        } else if (value > hi && value <= hihi) {
            value_class = 'value-warning';
        } else if (value < lowlow) {
            value_class = 'value-error';
        } else if (value < low && value >= lowlow) {
            value_class = 'value-warning';
        } else if (value > low && value < hi && !limits) {
            cp_class_dict[item_id + cp.id] = undefined;
            return cp_class_dict;
        } else if (value >= low && value <= hi && limits) {
            cp_class_dict[item_id + cp.id] = {class: 'value-ok', message: ''};
            return cp_class_dict;
        }

        let cpName: string = column?.title || cp.attributes.description || cp.attributes.name;
        let message: string = `Warning: value ${value} for ${cpName} `;

        if (value_class === "value-warning") {
            if (value >= lowlow && value < low) {
                message = `${message} is in the low limit range (${lowlow} - ${low})`;
            } else if (value > hi && value <= hihi) {
                message = `${message} is in the high limit range (${hi} - ${hihi})`;
            }

        } else if (value_class === "value-error") {
            message = `${message} is out of limits (${lowlow} - ${hihi})`;
        }

        cp_class_dict[item_id + cp.id] = {class: value_class, message: message};
        // this.changeDetectorRef.detectChanges();
        if (show_error) {
            this.notification.openError(message);
        }
        return cp_class_dict;

    }

    openFileFormatModal(event: any, downloadFunction, default_file_type: FileDownloadOption = 'formatted_csv',
                        relationshipType?: 'event_types' | 'component_types', modelIDs: ModelID[] = []) {

        const selectionChanged: (event) => void = (event) => {
            downloadFunction(event.fileType, event.fileConstantPropertyIDs);
        };
        const handle = this.popupService.open(ComponentEventsDownloadFormatMenuComponent, {
            x: event.clientX,
            y: event.clientY,
            inputs: {
                format: default_file_type,
                fileOptions: FILE_DOWNLOAD_OPTIONS,
                relationshipType: relationshipType,
                modelIDs: modelIDs,
            },
        });
        handle.componentRef.instance.handleDownload.subscribe((e: any) => {
            selectionChanged(e);
            handle.close();
        });

    }

    push_data_to_client(componentIds: string[], exporterType: ExporterType, clientExporterName: string) {
        let params = {
            "exporter_name": exporterType,
            "component_ids": componentIds,
        }
        if (clientExporterName) {
            params["extra_payload"] = {
                "client_exporter_name": clientExporterName
            }
        }
        return this.http.post('api/export_data', params).pipe(map((response: any) => response));
    }

    unlinkComponent(component: WireComponent, first_component: WireComponent): Observable<any> {
        return from(this.formDialogService.confirm("Are you sure you want to unlink this component?", "No", "Yes"))
            .pipe(
                switchMap(isConfirmed => {
                    if (!isConfirmed) {
                        return EMPTY;
                    }
                    this.notification.openInfo(`Removing ${component.attributes?.name} from ${first_component.attributes?.name}.`);

                    // TODO replace this logic with the relation upsert API, so all this logic can be handled by the backend.
                    const options = new SearchQueryOptions();
                    options.filters = [{
                        or: [{
                            and: [
                                getRelationWithIdFilter('first_component', first_component.id),
                                getRelationWithIdFilter('second_component', component.id),
                            ]
                        }, {
                            and: [
                                getRelationWithIdFilter('first_component', component.id),
                                getRelationWithIdFilter('second_component', first_component.id),
                            ]
                        }]
                    }, {name: 'id', op: 'ne', val: first_component.id}];
                    return this.api.component_component.searchSingle(options).pipe(concatMap(result => {
                        let component_component = result.data;
                        if (!component_component) {
                            return of(false);
                        }
                        return this.api.component_component.obsDelete(component_component.id);
                    }));
                })
            );
    }

    unlinkEvent(event: WireEvent, component: WireComponent): Observable<any> {
        return from(this.formDialogService.confirm("Are you sure you want to unlink this component?", "No", "Yes"))
            .pipe(
                switchMap(isConfirmed => {
                    if (!isConfirmed) {
                        return EMPTY;
                    }
                    this.notification.openInfo(`Removing event from " ${component.attributes?.name}.`);
                    return this.http.delete('/api/relation/event_component/' + event.id + '/' + component.id);
                }));
    }

    downloadFileFromJobID(jobID: ModelID, filename: string) {
        const headers = new HttpHeaders({
            'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
            'Pragma': 'no-cache',
            'Expires': '0'
        });

        this.http.get('/api/download/job/' + jobID + '/file', {headers: headers, responseType: 'blob'}).subscribe({
            next: (response: any) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(response);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }, error: (error) => {
                this.notification.openError('Failed to download component files: ' + error.reason);
                console.log('reason for fail', error);
            }
        });
    }

    pollComponentFilesJob(jobID: ModelID): void {
        this.jobService.createJobPoll(jobID)
            .subscribe((completedJob: JobPollResponse) => {
                if (completedJob.status) {
                    this.jobComplete$.next();
                    const snackBarRef = this.notification.openSuccess('Your file is ready!', null, 'Download');
                    snackBarRef.afterDismissed().subscribe(() => {
                        this.downloadFileFromJobID(jobID, completedJob.filename);
                    });
                } else if (!completedJob.processing) {
                    this.jobComplete$.next();
                    this.notification.openError('Error downloading files: ' + completedJob.message);
                }
            });
    }

    /**
     * POSTs a bulk file download job to the backend.
     *
     * Sends either `component_ids` (row-selection path) or `filters` +
     * optional `constant_date_filter` (filter path). The two paths are
     * mutually exclusive: when `componentIds` is non-empty it takes
     * precedence and `constantDateFilter` is ignored.
     *
     * On HTTP 400 the backend may indicate that the supplied filters matched
     * no components; in that case an info toast is shown instead of a generic
     * error.
     *
     * @param filters - RESTFilter to pass to the backend (filter path).
     * @param constantPropertyIDs - Constant property IDs to include in the zip.
     * @param componentIds - Explicit component IDs (row-selection path). When
     *   provided and non-empty, `filters` and `constantDateFilter` are not sent.
     * @param constantDateFilter - Optional server-side date-range filter spec.
     *   Sent alongside `filters` when the filter path is used.
     */
    downloadComponentFiles = (
        filters: RESTFilter,
        constantPropertyIDs: ModelID[],
        componentIds?: ModelID[],
        constantDateFilter?: ConstantDateFilter,
    ) => {
        const body: {
            constant_property_ids: ModelID[];
            component_ids?: ModelID[];
            filters?: RESTFilter;
            constant_date_filter?: ConstantDateFilter;
        } = {
            constant_property_ids: constantPropertyIDs,
        };
        if (componentIds?.length) {
            body.component_ids = componentIds;
        } else {
            body.filters = filters;
            if (constantDateFilter) {
                body.constant_date_filter = constantDateFilter;
            }
        }
        return this.http.post('/api/BulkComponentFileDownload/', body)
            .pipe(take(1))
            .subscribe({
                next: (resp: { message: string, job: ModelID }) => {
                    this.notification.openSuccess('Started file download job.');
                    this.pollComponentFilesJob(resp.job);
                },
                error: (err) => {
                    if (err?.status === 400) {
                        this.notification.openInfo('No components match the current filters.');
                    } else {
                        this.notification.openError('Failed to start file download job.');
                    }
                    this.jobComplete$.next();
                },
            });
    }

    // $validateConstantValue = new Subject<ConstantValue>;
    $validateConstantValue = new Subject<{ value: ConstantValue, validated: boolean }>;

    detectComponentConstantLimits($event, value: ConstantValue, limits: Partial<ModelWithLimits<ConstantPropertyComponentType>>, cpColumn: EventConfigColumn, component: Component, validate: boolean): void {
        const shouldValidate = limits?.[cpColumn.id] && validate;
        if (!shouldValidate || (shouldValidate && !detectLimits(limits[cpColumn.id], value).error)) {
            this.$validateConstantValue.next({value: value, validated: false});
            return;
        }

        const config = {
            component: 'LimitsPrompt',
            parameters: {
                limits: limits[cpColumn.id],
                value: value,
                cp_column: cpColumn,
                component: component
            }
        };
        const panelClass = 'prompt-dialog';

        let promptD = this.customDialogService.openCustomDialog($event, config, panelClass);
        promptD.afterClosed().subscribe(result => {
            this.$validateConstantValue.next({value: result, validated: true});
        });
    }
}
